// WebCrypto helpers shared by the Worker and the Tenant Durable Object (workerd).
// Both runtimes expose globalThis.crypto.subtle.

const enc = new TextEncoder()
const dec = new TextDecoder()

export function toBase64Url(bytes: Uint8Array): string {
  let bin = ''
  for (const b of bytes) bin += String.fromCharCode(b)
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export function fromBase64Url(s: string): Uint8Array {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4)
  const bin = atob(b64)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

export function randomKey(bytes = 32): string {
  return toBase64Url(crypto.getRandomValues(new Uint8Array(bytes)))
}

export function toHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

export async function sha256Hex(data: string | Uint8Array): Promise<string> {
  const buf = typeof data === 'string' ? enc.encode(data) : data
  return toHex(new Uint8Array(await crypto.subtle.digest('SHA-256', buf as BufferSource)))
}

async function hmacKey(keyB64: string): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', fromBase64Url(keyB64) as BufferSource, { name: 'HMAC', hash: 'SHA-256' }, false, [
    'sign',
    'verify',
  ])
}

export async function hmacHex(keyB64: string, message: string): Promise<string> {
  const sig = await crypto.subtle.sign('HMAC', await hmacKey(keyB64), enc.encode(message))
  return toHex(new Uint8Array(sig))
}

/** Constant-time comparison of two equal-length hex strings. */
export function timingSafeEqualHex(a: string, b: string): boolean {
  if (a.length !== b.length) return false
  let diff = 0
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i)
  return diff === 0
}

async function aesKey(keyB64: string): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', fromBase64Url(keyB64) as BufferSource, 'AES-GCM', false, ['encrypt', 'decrypt'])
}

/** AES-256-GCM. Output: base64url(iv[12] || ciphertext). */
export async function encrypt(keyB64: string, plaintext: string): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const ct = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, await aesKey(keyB64), enc.encode(plaintext)))
  const out = new Uint8Array(iv.length + ct.length)
  out.set(iv)
  out.set(ct, iv.length)
  return toBase64Url(out)
}

export async function decrypt(keyB64: string, box: string): Promise<string> {
  const raw = fromBase64Url(box)
  const iv = raw.slice(0, 12)
  const ct = raw.slice(12)
  return dec.decode(await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, await aesKey(keyB64), ct))
}
